<!--
  =====================================================================
  SECTION HERO - FULL WIDTH HERO IMAGE SECTION
  =====================================================================
  
  Hero section component with dynamic image switching capability.
  
  FEATURES:
  - Full viewport width hero section
  - Smooth image transitions with clip-path animation
  - Dynamic image switching via useHeroCoverSwitcher
  - Optional decorator image overlay
  - Fluid height: clamp(300px, 50vh, 600px) + max-height(100vh - 200px)
  - Guarantees above-the-fold visibility with header + triple title
  
  TRANSITIONS:
  - New image "reveals" from left to right using clip-path
  - Smooth 2s ease-out animation (cubic-bezier timing)
  - Old image stays visible during transition (no flicker)
  
  =====================================================================
-->

<template>
  <section class="section-hero" :class="{ 'has-mobile-video': props.content.mobileVideo }">

    <video
      v-if="props.content.mobileVideo"
      ref="videoRef"
      class="hero-video"
      :src="props.content.mobileVideo"
      autoplay
      muted
      playsinline
    />

    <transition name="hero-slide" mode="in-out">
      <div
        v-if="currentCover?.image"
        :key="currentCover.image"
        class="cover-layer"
        :style="{ backgroundImage: `url(${currentCover.image})` }"
      />
    </transition>

    <img
      v-if="props.content.decoratorImage"
      class="hero-decorator"
      :src="props.content.decoratorImage"
      alt=""
    />
  </section>
</template>

<script setup>
import { defineProps, ref, onMounted } from 'vue'
import { useHeroCoverSwitcher } from '@/utils/useHeroCoverSwitcher.js'

// ───────────────────────────────────────────────────────────────────
// PROPS
// ───────────────────────────────────────────────────────────────────

/**
 * Component props definition
 * @prop {Object} content - Section content with optional decoratorImage / mobileVideo
 */
const props = defineProps({
  content: { type: Object, required: true }
})

// ───────────────────────────────────────────────────────────────────
// COMPOSABLES
// ───────────────────────────────────────────────────────────────────

/** Gets current cover image from hero switcher utility */
const { currentCover } = useHeroCoverSwitcher()

// ───────────────────────────────────────────────────────────────────
// VIDEO AUTOPLAY (iOS Safari)
// ───────────────────────────────────────────────────────────────────

const videoRef = ref(null)

onMounted(() => {
  if (!videoRef.value) return
  const video = videoRef.value
  video.addEventListener('loadedmetadata', () => {
    video.play().catch(() => {})
  }, { once: true })
})
</script>

<style scoped lang="scss">
@import "@/assets/styles/partials/variables";

/* ------------------------------------------------------------------
   Hero section - Base layout
------------------------------------------------------------------ */
.section-hero {
  position: relative;
  width: 100vw;
  left: 50%;
  margin-left: -50vw;
  height: clamp(300px, 50vh, 600px);
  max-height: calc(100vh - 200px);
  overflow: hidden;
}

/* ------------------------------------------------------------------
   Cover layer - Background image container
------------------------------------------------------------------ */
.cover-layer {
  position: absolute;
  /* ricaduta iOS < 14.5: longhand prima, `inset` sotto vince sui moderni */
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  inset: 0;
  background: center 70% / cover no-repeat;
  
  &::before {
    content: '';
    position: absolute;
    /* ricaduta iOS < 14.5: longhand prima, `inset` sotto vince sui moderni */
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba($clr-tertiary, 0.1) 0%,
      rgba($clr-tertiary, 0.5) 100%
    );
    pointer-events: none;
  }
}

/* ------------------------------------------------------------------
   Hero transitions - Image reveal animation
   ───────────────────────────────────────────────────────────────────
   Enter (new image):  clip 100% → 0%  (left to right reveal)
   Leave (old image):  stays visible, no animation (no flicker)
------------------------------------------------------------------ */
.hero-slide-enter-from {
  clip-path: inset(0 100% 0 0);
}

.hero-slide-enter-to {
  clip-path: inset(0 0 0 0);
}

.hero-slide-enter-active {
  transition: clip-path 2s cubic-bezier(0.22, 1, 0.36, 1);
}

.hero-slide-leave-from,
.hero-slide-leave-to {
  clip-path: inset(0 0 0 0);
}

.hero-slide-leave-active {
  transition: none;
}

/* ------------------------------------------------------------------
   Mobile video - Sostituisce l'immagine su mobile
------------------------------------------------------------------ */
.hero-video {
  display: none;
  position: absolute;
  /* ricaduta iOS < 14.5: longhand prima, `inset` sotto vince sui moderni */
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: left top; // mobile: inquadratura ancorata in alto a sinistra
}

@media (max-width: 1024px) {
  .has-mobile-video {
    .hero-video {
      display: block;
    }

    .cover-layer {
      display: none;
    }

    // Stesso filtro verde del desktop, sovrapposto al video
    &::after {
      content: '';
      position: absolute;
      /* ricaduta iOS < 14.5: longhand prima, `inset` sotto vince sui moderni */
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      inset: 0;
      z-index: 1;
      background: linear-gradient(
        to bottom,
        rgba($clr-tertiary, 0.1) 0%,
        rgba($clr-tertiary, 0.5) 100%
      );
      pointer-events: none;
    }

    .hero-decorator {
      z-index: 2; // resta sopra il filtro verde
    }
  }
}

/* ------------------------------------------------------------------
   Decorator image - Bottom overlay
------------------------------------------------------------------ */
.hero-decorator {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  pointer-events: none;
  
  &::after {
    content: '';
    position: absolute;
    /* ricaduta iOS < 14.5: longhand prima, `inset` sotto vince sui moderni */
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba($clr-tertiary, 0.1) 0%,
      rgba($clr-tertiary, 0.5) 100%
    );
    pointer-events: none;
  }
}
</style>